<template>
  <div v-if="dish" class="overlay" :class="{ wide: store.isWide }" @click="closeDish">
    <div class="card" :class="{ wide: store.isWide }" @click.stop>
      <!-- Hero -->
      <div class="hero" :style="heroStyle">
        <span class="hero-initials">{{ initials }}</span>
        <button class="close" @click="closeDish">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--seth-navy)" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>

      <div class="content">
        <div class="name">{{ name }}</div>

        <!-- Banner -->
        <div v-if="status !== 'neutral'" class="banner" :class="`s-${status}`">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path :d="bannerIcon" /></svg>
          <span>{{ bannerText }}</span>
        </div>

        <!-- Contiene -->
        <div v-if="contains.length" class="block">
          <div class="eyebrow">{{ ui.alergenosT }}</div>
          <div class="rows">
            <div v-for="a in contains" :key="a.id" class="row" :class="{ mine: a.mine, danger: a.mine }">
              <span class="row-icon"><allergen-image :id="a.id" :size="26" /></span>
              <span class="row-name">{{ a.name }}</span>
              <span v-if="a.mine" class="tag danger">{{ ui.evitar }}</span>
            </div>
          </div>
        </div>
        <div v-else class="no-contains">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
          {{ ui.sinAlergenosDecl }}
        </div>

        <!-- Trazas -->
        <div v-if="trazas.length" class="block">
          <div class="eyebrow">{{ ui.trazasT }}</div>
          <div class="rows">
            <div v-for="a in trazas" :key="a.id" class="row" :class="{ mine: a.mine, warning: a.mine }">
              <span class="row-icon"><allergen-image :id="a.id" :size="26" /></span>
              <span class="row-name">{{ a.name }}</span>
              <span v-if="a.mine" class="tag warning">{{ ui.ojo }}</span>
            </div>
          </div>
        </div>

        <div class="block">
          <app-button variant="secondary" pill block @click="closeDish">{{ ui.cerrar }}</app-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PAL, UI, resolveAllergen, initialsOf } from '@/data'
import { dishStatus, bannerFor } from '@/viewmodel'
import { store, closeDish } from '@/store'
import AllergenImage from './ui/AllergenImage.vue'
import AppButton from './ui/AppButton.vue'

const WARN_ICON = 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z M12 9v4 M12 17h.01'
const CHECK_ICON = 'M20 6 9 17l-5-5'

export default {
  name: 'DishDetail',
  components: { AllergenImage, AppButton },
  data() {
    return { store }
  },
  computed: {
    ui() { return UI[store.locale] || UI.es },
    dish() {
      if (store.detailId == null) return null
      return store.escandalls.find(d => d.id === store.detailId) || null
    },
    name() { return this.dish ? (this.dish.nombre || '—') : '' },
    initials() { return initialsOf(this.dish && this.dish.nombre) },
    palette() { return PAL[(this.dish ? this.dish.id : 0) % 4] },
    heroStyle() { return { background: this.palette[0], color: this.palette[1] } },
    algIds() { return (this.dish && this.dish.alergeno_ids) || [] },
    tzIds() { return (this.dish && this.dish.traza_ids) || [] },
    status() { return dishStatus(this.algIds, this.tzIds, store.mine) },
    bannerText() {
      return bannerFor(this.status, this.algIds, this.tzIds, store.mine, store.locale, store.alergenosMap)
    },
    bannerIcon() { return this.status === 'safe' ? CHECK_ICON : WARN_ICON },
    contains() {
      return this.algIds.map(id => {
        const a = resolveAllergen(id, store.locale, store.alergenosMap)
        return { ...a, mine: store.mine.includes(id) }
      })
    },
    trazas() {
      return this.tzIds.map(id => {
        const a = resolveAllergen(id, store.locale, store.alergenosMap)
        return { ...a, mine: store.mine.includes(id) }
      })
    }
  },
  watch: {
    // Bloquea el scroll del fondo mientras el detalle está abierto.
    dish(val) {
      document.body.style.overflow = val ? 'hidden' : ''
    }
  },
  beforeUnmount() {
    document.body.style.overflow = ''
  },
  methods: { closeDish }
}
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(0, 26, 46, 0.45);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding: 0;
  animation: seth-fade 200ms ease;
}
.overlay.wide { align-items: center; padding: 24px; }

.card {
  background: #fff;
  width: 100%;
  max-width: 100%;
  max-height: 92vh;
  overflow: auto;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 24px 60px rgba(0, 26, 46, 0.22);
  animation: seth-up 280ms cubic-bezier(0.22, 0.61, 0.36, 1);
}
.card.wide { max-width: 560px; max-height: 88vh; border-radius: 18px; }

.hero {
  position: relative;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.hero-initials { font-family: var(--font-display); font-weight: 600; font-size: 2.4rem; opacity: 0.85; }
.close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(20, 36, 46, 0.12);
}

.content { padding: 20px clamp(18px, 4vw, 26px) 24px; }
.name {
  font-family: var(--font-display);
  font-weight: 300;
  font-size: 1.6rem;
  color: var(--seth-navy);
  line-height: 1.15;
}

.banner {
  margin-top: 16px;
  display: flex;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 12px;
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.4;
}
.banner svg { flex-shrink: 0; margin-top: 1px; }
.banner.s-danger { background: #F6E2E2; color: #B23A3A; }
.banner.s-warning { background: #F6ECD8; color: #9A6A1C; }
.banner.s-safe { background: #D6F0E8; color: #00805F; }

.block { margin-top: 22px; }
.eyebrow {
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--seth-gold-600);
  font-weight: 600;
  margin-bottom: 10px;
}
.rows { display: flex; flex-direction: column; gap: 8px; }
.row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #E8E1D5;
}
.row.danger { background: #F6E2E2; border-color: #E7B9B9; }
.row.warning { background: #F6ECD8; border-color: #E6CFA3; }
.row-icon { display: flex; color: #3A4A53; flex-shrink: 0; }
.row-icon.danger { color: #B23A3A; }
.row-icon.warning { color: #C4862A; }
.row-name { font-size: 0.95rem; font-weight: 600; color: var(--ink-900); }
.tag {
  margin-left: auto;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.tag.danger { color: var(--danger); }
.tag.warning { color: var(--warning); }

.no-contains {
  margin-top: 22px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--seth-green-100);
  color: var(--seth-green-700);
  padding: 14px 16px;
  border-radius: 12px;
  font-size: 0.92rem;
  font-weight: 600;
}

</style>
